import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import ClassOverviewSection from "../components/ClassOverviewSection";
import ProgramStatusPanel from "../components/ProgramStatusPanel";
import TodayAbsences from "../components/admin/TodayAbsences";
import { ColorDot } from "../components/ProgressBar";
import { Settings, BookMarked, Gavel, FileSpreadsheet, ClipboardCheck, SlidersHorizontal, CalendarDays, TrendingUp, History } from "lucide-react";
import { toast, Toaster } from "sonner";
import { downloadBlob } from "../lib/download";

const NAV_LINKS = [
  { to: "/emplois-du-temps", icon: CalendarDays, label: "Emplois du temps", testId: "timetables-link" },
  { to: "/admin/progressions", icon: TrendingUp, label: "Suivi des progressions", testId: "progressions-link" },
  { to: "/admin/synthese", icon: SlidersHorizontal, label: "Synthèse & filtres", testId: "synthese-link" },
  { to: "/admin/appels", icon: ClipboardCheck, label: "Suivi des appels", testId: "appels-overview-link" },
  { to: "/admin/sanctions", icon: Gavel, label: "Sanctions & remarques", testId: "sanctions-link" },
  { to: "/admin/mercredis", icon: BookMarked, label: "Suivi des mercredis", testId: "mercredis-link" },
  { to: "/teacher/etude", icon: ClipboardCheck, label: "Appel de l'étude & rattrapages", testId: "etude-appel-link" },
];

function AdminNav({ globalXlsBusy, onGlobalExcel }) {
  const [recidivesBusy, setRecidivesBusy] = useState(false);

  const downloadRecidives = async () => {
    setRecidivesBusy(true);
    await downloadBlob("/rattrapages/recidives.pdf", "bulletin-recidives-rattrapages.pdf", "Bulletin des récidives téléchargé", "Échec de la génération du bulletin des récidives");
    setRecidivesBusy(false);
  };

  return (
    <div className="flex flex-wrap gap-3">
      <button
        onClick={onGlobalExcel}
        disabled={globalXlsBusy}
        className="btn-secondary !py-2.5 !px-5 text-sm flex items-center gap-2"
        title="Assiduité & signalements de toutes les classes en un seul fichier"
        data-testid="global-xlsx-btn"
      >
        <FileSpreadsheet size={16} /> {globalXlsBusy ? "Export..." : "Excel global"}
      </button>
      <button
        onClick={downloadRecidives}
        disabled={recidivesBusy}
        className="btn-secondary !py-2.5 !px-5 text-sm flex items-center gap-2"
        title="Élèves convoqués 2 fois ou plus en rattrapage du mercredi"
        data-testid="recidives-pdf-btn"
      >
        <History size={16} /> {recidivesBusy ? "Génération..." : "Récidives rattrapages"}
      </button>
      {NAV_LINKS.map(({ to, icon: Icon, label, testId }) => (
        <Link key={to} to={to} className="btn-secondary !py-2.5 !px-5 text-sm flex items-center gap-2" data-testid={testId}>
          <Icon size={16} /> {label}
        </Link>
      ))}
      <Link to="/admin/gestion" className="btn-primary !py-2.5 !px-5 text-sm flex items-center gap-2" data-testid="manage-school-link">
        <Settings size={16} /> Gestion de l'établissement
      </Link>
    </div>
  );
}

function StatStrip({ overview }) {
  const totalSubjects = overview.reduce((acc, c) => acc + c.subjects.length, 0);
  const counts = overview.reduce(
    (acc, c) => {
      c.subjects.forEach((s) => {
        acc[s.progress.color] = (acc[s.progress.color] || 0) + 1;
      });
      return acc;
    },
    { ok: 0, just: 0, late: 0 }
  );
  return (
    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 pb-10 border-b border-[var(--parchment-2)]">
      <Stat label="Classes" value={overview.length} testId="stat-classes" />
      <Stat label="Matières suivies" value={totalSubjects} testId="stat-subjects" />
      <Stat label="À jour" value={counts.ok} dot="ok" testId="stat-ok" />
      <Stat label="En retard" value={counts.late + counts.just} dot={counts.late > 0 ? "late" : "just"} testId="stat-late" />
    </div>
  );
}

function useDashboardDownloads() {
  const [pdfBusy, setPdfBusy] = useState(null);
  const [xlsBusy, setXlsBusy] = useState(null);
  const [globalXlsBusy, setGlobalXlsBusy] = useState(false);

  const downloadPdf = async (cls) => {
    setPdfBusy(cls.id);
    await downloadBlob(`/admin/classes/${cls.id}/report.pdf`, `rapport-${cls.name.replace(/\s+/g, "-")}.pdf`, `Rapport ${cls.name} téléchargé`, "Échec de la génération du rapport PDF");
    setPdfBusy(null);
  };

  const downloadExcel = async (cls) => {
    setXlsBusy(cls.id);
    await downloadBlob(`/admin/classes/${cls.id}/export.xlsx`, `assiduite-${cls.name.replace(/\s+/g, "-")}.xlsx`, `Export Excel ${cls.name} téléchargé`, "Échec de l'export Excel");
    setXlsBusy(null);
  };

  const downloadGlobalExcel = async () => {
    setGlobalXlsBusy(true);
    await downloadBlob("/admin/export.xlsx", "assiduite-etablissement.xlsx", "Export Excel de l'établissement téléchargé", "Échec de l'export Excel global");
    setGlobalXlsBusy(false);
  };

  return { pdfBusy, xlsBusy, globalXlsBusy, downloadPdf, downloadExcel, downloadGlobalExcel };
}

export default function AdminDashboard() {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const { pdfBusy, xlsBusy, globalXlsBusy, downloadPdf, downloadExcel, downloadGlobalExcel } = useDashboardDownloads();

  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => {
      setOverview(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />

      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow">Administration</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Tableau de bord global</h1>
            <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
              Vue d'ensemble de l'avancement du programme par classe et par matière.
              Cliquez sur une matière pour entrer dans l'espace professeur correspondant.
            </p>
          </div>
          <AdminNav globalXlsBusy={globalXlsBusy} onGlobalExcel={downloadGlobalExcel} />
        </div>

        <StatStrip overview={overview} />

        <TodayAbsences />

        {loading ? (
          <div className="mt-12 text-[var(--ink-soft)]">Chargement...</div>
        ) : (
          <>
            <ProgramStatusPanel overview={overview} />
            <div className="mt-10 space-y-10" data-testid="admin-classes-list">
              {overview.map((cls) => (
                <ClassOverviewSection key={cls.class.id} cls={cls} pdfBusy={pdfBusy} onDownloadPdf={downloadPdf} xlsBusy={xlsBusy} onDownloadExcel={downloadExcel} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, dot, testId }) {
  return (
    <div data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className="font-serif-display text-4xl font-semibold mt-1 flex items-center gap-2">
        {value}
        {dot && <ColorDot color={dot} />}
      </div>
    </div>
  );
}
